'use client';

import { Award, Mountain, Rocket, ShieldCheck, Sprout } from 'lucide-react';

export interface StarterScan {
  id: string;
  label: string;
  hint: string;
  icon: typeof Award;
  params: Record<string, string>;
}

// Tiny companies top every ranking with one-off numbers (ROE of 1,000%, 200% in 3 months), so
// the scans start from companies worth ₹500 crore or more; the chip shows it and can be removed.
const MIN_SIZE_CR = '500';

// Each scan is only URL query params, so it can be shared and tweaked like any other filter.
export const STARTER_SCANS: StarterScan[] = [
  {
    id: 'quality',
    label: 'Profitable & low debt',
    hint: 'Biggest companies earning over 15% on shareholders’ money and owing less than half of it',
    icon: ShieldCheck,
    // A negative debt-to-equity means losses wiped out the equity, so it is excluded, not "low".
    params: {
      view: 'valuation',
      roe_pct_min: '15',
      npm_pct_min: '0',
      debt_to_equity_min: '0',
      debt_to_equity_max: '0.5',
      market_cap_cr_min: MIN_SIZE_CR,
      sort: 'market_cap_cr',
      order: 'desc',
    },
  },
  {
    id: 'near-high',
    label: 'Near 52-week high',
    hint: 'Trading closest to their highest price of the past year',
    icon: Mountain,
    params: { view: 'performance', market_cap_cr_min: MIN_SIZE_CR, sort: 'high_52w_distance_pct', order: 'asc' },
  },
  {
    id: 'momentum',
    label: 'Strong momentum',
    hint: 'Up more than 15% in the last 3 months',
    icon: Rocket,
    params: {
      view: 'performance',
      returns_3m_pct_min: '15',
      market_cap_cr_min: MIN_SIZE_CR,
      sort: 'returns_3m_pct',
      order: 'desc',
    },
  },
  {
    id: 'growth',
    label: 'Fast-growing sales',
    hint: 'Revenue up more than 25% over the last 12 months',
    icon: Sprout,
    params: {
      view: 'growth',
      revenue_growth_ttm_pct_min: '25',
      market_cap_cr_min: MIN_SIZE_CR,
      sort: 'revenue_growth_ttm_pct',
      order: 'desc',
    },
  },
  {
    id: 'top-scores',
    label: 'Top scores',
    hint: 'Highest overall 0–100 rating first',
    icon: Award,
    params: { view: 'scores', market_cap_cr_min: MIN_SIZE_CR, sort: 'score', order: 'desc' },
  },
];

export function isScanActive(scan: StarterScan, searchParams: URLSearchParams): boolean {
  return Object.entries(scan.params).every(([key, value]) => searchParams.get(key) === value);
}

interface StarterScansProps {
  searchParams: URLSearchParams;
  onSelect: (scan: StarterScan | null) => void;
}

export default function StarterScans({ searchParams, onSelect }: StarterScansProps) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        New here? Try a ready-made scan
      </p>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 lg:grid-cols-5 [scrollbar-width:none]">
        {STARTER_SCANS.map((scan) => {
          const active = isScanActive(scan, searchParams);
          const Icon = scan.icon;
          return (
            <button
              key={scan.id}
              type="button"
              aria-pressed={active}
              onClick={() => onSelect(active ? null : scan)}
              className={`flex w-60 shrink-0 flex-col items-start gap-1 rounded-xl border px-3.5 py-2.5 text-left transition-colors sm:w-auto ${
                active
                  ? 'border-emerald-500 bg-emerald-500/10'
                  : 'border-zinc-200 bg-white hover:border-emerald-500/50 dark:border-zinc-700 dark:bg-zinc-900'
              }`}
            >
              <span
                className={`flex items-center gap-2 text-sm font-semibold ${
                  active ? 'text-emerald-700 dark:text-emerald-300' : 'text-zinc-800 dark:text-zinc-100'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0 text-emerald-500" />
                {scan.label}
              </span>
              <span className="text-xs leading-snug text-zinc-500 dark:text-zinc-400">{scan.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
